import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SP-3 — the same loose match the server uses before it answers 409: case,
// spacing and punctuation do not make two rooms.
const key = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
export const sameRoomName = (a, b) => key(a) === key(b);

/**
 * SP-3 — one hook for the project's room list. `rooms` are real rooms (id);
 * `suggestions` are names seen on the Takeoff, the estimate or the clips that are
 * not rooms yet (id null). `createRoom` adds a room and answers the stored row;
 * a name that already exists answers the existing row. Every mounted hook on the
 * same project reloads when any of them adds a room.
 */
export function useProjectRooms(projectId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) { setRows([]); return []; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/rooms`);
      const list = (data.rooms || []).filter((r) => r && r.name);
      setRows(list);
      return list;
    } catch (error) {
      setRows([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onChanged = (e) => {
      if (!projectId) return;
      if (!e.detail || !e.detail.projectId || e.detail.projectId === projectId) load();
    };
    window.addEventListener('km-rooms-changed', onChanged);
    return () => window.removeEventListener('km-rooms-changed', onChanged);
  }, [load, projectId]);

  const rooms = useMemo(() => rows.filter((r) => r.id), [rows]);
  const suggestions = useMemo(() => rows.filter((r) => !r.id), [rows]);

  const createRoom = useCallback(async (name, suggestion = null) => {
    const wanted = String(name || '').trim();
    if (!projectId || !wanted) return null;
    const have = rows.find((r) => r.id && sameRoomName(r.name, wanted));
    if (have) return have;
    const source = suggestion?.source === 'floor_plan' ? 'takeoff'
      : suggestion?.source === 'clips' ? 'clips'
        : suggestion?.source === 'estimate' ? 'estimate' : 'typed';
    try {
      const { data } = await axios.post(`${API}/projects/${projectId}/rooms`, {
        name: wanted,
        measurement_id: suggestion?.measurement_id || null,
        floor_plan_id: suggestion?.floor_plan_id || null,
        source,
      });
      const room = data.room || null;
      if (room) {
        setRows((prev) => [room, ...prev.filter((r) => !sameRoomName(r.name, room.name))]);
        window.dispatchEvent(new CustomEvent('km-rooms-changed', { detail: { projectId } }));
      }
      return room;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409) {
        // The old door answers {message, room}; the SP-1c door answers a sentence.
        if (detail && typeof detail === 'object' && detail.room?.id) return detail.room;
        const fresh = await load();
        const existing = fresh.find((r) => r.id && sameRoomName(r.name, wanted));
        if (existing) return existing;
      }
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not add that room'));
      return null;
    }
  }, [projectId, rows, load]);

  return { rooms, suggestions, all: rows, loading, reload: load, createRoom };
}

export default useProjectRooms;
